import React from 'react';
import EVSEChecklist from './EVSEChecklist';
import EVSEForm from './EVSEForm';
import { CHARGER_REQUIRED_FIELDS, CHARGER_FIELD_GROUPS } from '../config/evseFields';

const IconPlus = () => (
  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const IconTrash = () => (
  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
  </svg>
);

// Each charger entry in the wizard's in-memory state looks like:
// { tempId, selectedFields: [], formData: {}, errors: {} }

export default function WizardStepChargers({ chargers, setChargers }) {

  const addCharger = () => {
    setChargers(prev => [
      ...prev,
      { tempId: Date.now(), selectedFields: [], formData: {}, errors: {} }
    ]);
  };

  const removeCharger = (tempId) => {
    setChargers(prev => prev.filter(c => c.tempId !== tempId));
  };

  const updateCharger = (tempId, updates) => {
    setChargers(prev => prev.map(c => c.tempId === tempId ? { ...c, ...updates } : c));
  };

  const toggleField = (tempId, name) => {
    const charger = chargers.find(c => c.tempId === tempId);
    const isSelected = charger.selectedFields.includes(name);
    const newSelectedFields = isSelected
      ? charger.selectedFields.filter(f => f !== name)
      : [...charger.selectedFields, name];

    const newFormData = { ...charger.formData };
    if (isSelected) delete newFormData[name];

    updateCharger(tempId, { selectedFields: newSelectedFields, formData: newFormData });
  };

  const selectAllInGroup = (tempId, names) => {
    const charger = chargers.find(c => c.tempId === tempId);
    updateCharger(tempId, {
      selectedFields: Array.from(new Set([...charger.selectedFields, ...names]))
    });
  };

  const clearAllInGroup = (tempId, names) => {
    const charger = chargers.find(c => c.tempId === tempId);
    const newFormData = { ...charger.formData };
    names.forEach(n => delete newFormData[n]);
    updateCharger(tempId, {
      selectedFields: charger.selectedFields.filter(f => !names.includes(f)),
      formData: newFormData
    });
  };

  const handleFieldChange = (tempId, name, value) => {
    const charger = chargers.find(c => c.tempId === tempId);
    const newErrors = { ...charger.errors };
    if (newErrors[name]) delete newErrors[name];
    updateCharger(tempId, {
      formData: { ...charger.formData, [name]: value },
      errors: newErrors
    });
  };

  return (
    <>
      {chargers.length === 0 && (
        <div className="card empty-state" style={{ marginBottom: 20 }}>
          <h3>No chargers added yet</h3>
          <p>Add at least one charger to this location to continue.</p>
        </div>
      )}

      {chargers.map((charger, index) => (
        <div className="entity-card" key={charger.tempId}>
          <div className="entity-card-header">
            <span className="entity-card-title">
              Charger {index + 1}
              {charger.formData.ocpp_id && (
                <span className="entity-card-badge">{charger.formData.ocpp_id}</span>
              )}
            </span>
            <button
              type="button"
              className="entity-remove-btn"
              onClick={() => removeCharger(charger.tempId)}
              title="Remove this charger"
            >
              <IconTrash />
            </button>
          </div>

          <div className="evse-builder">
            <EVSEChecklist
              groups={CHARGER_FIELD_GROUPS}
              selectedFields={charger.selectedFields}
              onToggle={(name) => toggleField(charger.tempId, name)}
              onSelectAllInGroup={(names) => selectAllInGroup(charger.tempId, names)}
              onClearAllInGroup={(names) => clearAllInGroup(charger.tempId, names)}
            />
            <EVSEForm
              title={`Charger ${index + 1} Details`}
              requiredFields={CHARGER_REQUIRED_FIELDS}
              groups={CHARGER_FIELD_GROUPS}
              selectedFields={charger.selectedFields}
              formData={charger.formData}
              onChange={(name, value) => handleFieldChange(charger.tempId, name, value)}
              errors={charger.errors}
              emptyHint="Tick a box on the left to add more details about this charger."
            />
          </div>
        </div>
      ))}

      <button type="button" className="add-entity-btn" onClick={addCharger}>
        <IconPlus /> Add Charger
      </button>
    </>
  );
}